import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../App';
import {
  Question, Plus, PaperPlaneTilt, PlayCircle, PencilSimple, Trash, CaretDown, CaretUp,
  ThumbsUp, ThumbsDown, ArrowRight, Compass, Record,
} from '@phosphor-icons/react';
import ListSearch from '../../components/ListSearch';
import { ModuleShell, SkeletonBlock, EmptyState } from '../../components/module-ui';
import HelpVideoPlayer from './HelpVideoPlayer';
import HelpArticleForm from './HelpArticleForm';
import HelpWalkthrough from './HelpWalkthrough';
import { startHelpTour } from './tours/HelpTourRunner';
import { getTour } from './tours/tourDefinitions';
import { canRecordTours } from './tours/tourRecorder';
import HelpPagination, { usePaged } from './HelpPagination';
import { UnansweredPanel, TicketSuggestionsPanel } from './HelpAdminPanels';
import {
  SOURCE_LABELS, askHelp, deleteHelpArticle, listHelpTopics, openHelpTopic, sendHelpFeedback,
} from './helpApi';

const cardStyle = { background: 'white', border: '1px solid #E5E7EB', padding: '12px' };
const mutedText = { fontSize: '11px', color: '#6B7280' };

function TopicBody({ topic, showWalkthrough = false, canManage = false }) {
  const [walkthrough, setWalkthrough] = useState(showWalkthrough);
  const hasTour = Boolean(topic.tour && getTour(topic.tour));
  // Topics with a tour get the real-page tutorial; the rest get the slide video.
  const canWalkthrough = topic.source !== 'live' && !hasTour;
  const canRecord = canManage && hasTour && canRecordTours() && Boolean(topic.help_id || topic.guide_id);

  useEffect(() => setWalkthrough(showWalkthrough), [topic.id, showWalkthrough]);

  return (
    <>
      <p style={{ fontSize: '12px', color: '#374151', whiteSpace: 'pre-wrap', lineHeight: '1.6', margin: 0 }}>
        {topic.answer}
      </p>
      {topic.video && (
        <div style={{ marginTop: '10px', maxWidth: '720px' }}>
          <HelpVideoPlayer video={topic.video} />
        </div>
      )}
      {canWalkthrough && (
        <div style={{ marginTop: '10px', maxWidth: '640px' }}>
          {walkthrough ? (
            <HelpWalkthrough title={topic.title} answer={topic.answer} linkLabel={topic.link_label} autoPlay={false} />
          ) : (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setWalkthrough(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}
            >
              <PlayCircle size={16} weight="fill" color="#2563EB" /> Watch step-by-step video
            </button>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
        {hasTour && (
          <>
            <button
              type="button"
              className="btn-primary"
              onClick={() => startHelpTour(topic.tour, { mode: 'video' })}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}
              data-testid="help-watch-tutorial"
            >
              <PlayCircle size={14} weight="fill" /> Watch tutorial video
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => startHelpTour(topic.tour)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}
              data-testid="help-view-tour"
            >
              <Compass size={14} weight="bold" /> Step by step
            </button>
          </>
        )}
        {topic.link && (
          <Link
            to={topic.link}
            className="btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}
          >
            {topic.link_label || 'Open page'} <ArrowRight size={12} />
          </Link>
        )}
        {canRecord && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => startHelpTour(topic.tour, { mode: 'record', topic })}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#DC2626' }}
            title="Plays the tutorial and records this tab — you review it before it is saved"
          >
            <Record size={14} weight="fill" /> Record tutorial video
          </button>
        )}
      </div>
    </>
  );
}

function TopicCard({ topic, canManage, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const isOverride = topic.source === 'guide' && topic.help_id;
  return (
    <div style={{ ...cardStyle, opacity: topic.published === false ? 0.7 : 1 }} data-testid="help-topic-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
          background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#111827' }}>
          {topic.video && <PlayCircle size={16} weight="fill" color="#2563EB" />}
          {topic.title}
          {topic.published === false && <span style={{ fontSize: '11px', color: '#F59E0B', fontWeight: '500' }}>(Draft)</span>}
        </span>
        {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
      </button>

      {open && (
        <div style={{ marginTop: '10px' }}>
          <TopicBody topic={topic} canManage={canManage} />
          {canManage && topic.source !== 'generated' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
              <span style={mutedText}>{SOURCE_LABELS[topic.source] || topic.source}{isOverride ? ' (edited)' : ''}</span>
              <button type="button" className="btn-secondary" onClick={() => onEdit(topic)} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <PencilSimple size={14} /> Edit
              </button>
              {topic.help_id && (
                <button type="button" className="btn-secondary" onClick={() => onDelete(topic)} style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#DC2626' }}>
                  <Trash size={14} /> {isOverride ? 'Reset to built-in' : 'Delete'}
                </button>
              )}
            </div>
          )}
          {canManage && topic.source === 'generated' && (
            <p style={{ ...mutedText, marginTop: '10px' }}>
              Built automatically from current settings — it updates itself when those settings change.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function AnswerPanel({ result, canRaiseTicket, canManage, onOpenTopic }) {
  const [feedback, setFeedback] = useState(null);

  useEffect(() => setFeedback(null), [result]);

  const sendFeedback = async (helpful) => {
    setFeedback(helpful ? 'up' : 'down');
    try {
      await sendHelpFeedback({
        question: result.question,
        entry_id: result.entry?.id,
        entry_title: result.entry?.title,
        helpful,
      });
    } catch {
      /* feedback is best-effort */
    }
  };

  const raiseTicket = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', fontSize: '12px', color: '#6B7280' }}>
      <span>Still need help?</span>
      {canRaiseTicket ? (
        <Link to="/dashboard/tickets/new" className="btn-secondary">Raise a ticket</Link>
      ) : (
        <span>Contact your support team.</span>
      )}
    </div>
  );

  if (result.kind === 'none') {
    return (
      <div style={{ ...cardStyle, borderLeft: '3px solid #F59E0B', marginBottom: '16px' }} data-testid="help-answer">
        <p style={{ fontSize: '12px', color: '#374151', margin: 0 }}>
          Sorry, there is no answer for that yet. Try different words, browse the topics below, or raise a ticket.
          Your question has been passed to the administrators so an answer can be added.
        </p>
        {raiseTicket}
      </div>
    );
  }

  if (result.kind === 'suggestions') {
    return (
      <div style={{ ...cardStyle, borderLeft: '3px solid #2563EB', marginBottom: '16px' }} data-testid="help-answer">
        <p style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>Did you mean:</p>
        <div style={{ display: 'grid', gap: '6px' }}>
          {result.suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              className="btn-secondary"
              onClick={() => onOpenTopic(s.id)}
              style={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'space-between' }}
            >
              <span>{s.video && <PlayCircle size={14} weight="fill" color="#2563EB" style={{ verticalAlign: 'middle', marginRight: '4px' }} />}{s.title}</span>
              <ArrowRight size={12} />
            </button>
          ))}
        </div>
        {raiseTicket}
      </div>
    );
  }

  const { entry } = result;
  return (
    <div style={{ ...cardStyle, borderLeft: '3px solid #2563EB', marginBottom: '16px' }} data-testid="help-answer">
      <h3 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>{entry.title}</h3>
      <TopicBody topic={entry} showWalkthrough canManage={canManage} />
      <p style={{ ...mutedText, marginTop: '8px' }}>Source: {SOURCE_LABELS[entry.source] || entry.source}</p>

      {result.related?.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <p style={{ fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Related</p>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {result.related.map((r) => (
              <button key={r.id} type="button" className="btn-secondary" style={{ fontSize: '12px' }} onClick={() => onOpenTopic(r.id)}>
                {r.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {entry.source !== 'live' && result.question && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', fontSize: '12px', color: '#6B7280' }}>
          {feedback ? (
            <span>{feedback === 'up' ? 'Thanks for the feedback!' : 'Thanks — we will improve this answer.'}</span>
          ) : (
            <>
              <span>Was this helpful?</span>
              <button type="button" className="btn-secondary" onClick={() => sendFeedback(true)} aria-label="Helpful" style={{ display: 'flex', alignItems: 'center' }}>
                <ThumbsUp size={14} />
              </button>
              <button type="button" className="btn-secondary" onClick={() => sendFeedback(false)} aria-label="Not helpful" style={{ display: 'flex', alignItems: 'center' }}>
                <ThumbsDown size={14} />
              </button>
            </>
          )}
        </div>
      )}
      {raiseTicket}
    </div>
  );
}

function HelpPage() {
  const { user } = useAuth();
  const canManage = ['super_admin', 'admin'].includes(user?.role);
  const canRaiseTicket = (user?.allowed_modules || []).includes('tickets');

  const [tab, setTab] = useState('topics');
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [result, setResult] = useState(null);
  const [askError, setAskError] = useState('');
  // { initial, helpId, guideId, heading, onSavedExtra }
  const [editor, setEditor] = useState(null);

  const fetchTopics = useCallback(async () => {
    try {
      setTopics(await listHelpTopics());
    } catch (err) {
      console.error('Failed to load help topics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTopics();
    // A recorded tutorial was saved to a topic.
    window.addEventListener('help-topics:changed', fetchTopics);
    return () => window.removeEventListener('help-topics:changed', fetchTopics);
  }, [fetchTopics]);

  // Tours reachable from this user's topics — already filtered by role and modules.
  const tours = useMemo(() => {
    const ids = Array.from(new Set(topics.map((t) => t.tour).filter(Boolean)));
    return ids.map((id) => ({ id, tour: getTour(id) })).filter((t) => t.tour);
  }, [topics]);

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(topics.map((t) => t.category || 'General')))],
    [topics],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return topics.filter((t) => {
      if (category !== 'All' && (t.category || 'General') !== category) return false;
      if (!q) return true;
      return [t.title, t.answer, t.category, ...(t.tags || [])].join(' ').toLowerCase().includes(q);
    });
  }, [topics, search, category]);
  const topicPages = usePaged(filtered, `${search}|${category}`);

  const runAsk = async (fn) => {
    setAsking(true);
    setAskError('');
    try {
      setResult(await fn());
    } catch (err) {
      setAskError(err?.response?.data?.detail || 'Could not get an answer. Please try again.');
    } finally {
      setAsking(false);
    }
  };

  const handleAsk = (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q) return;
    setResult(null);
    runAsk(() => askHelp(q));
  };

  const openTopic = (entryId) => runAsk(() => openHelpTopic(entryId, result?.question || question));

  const editTopic = (topic) => {
    setEditor({
      initial: {
        question: topic.title,
        answer: topic.answer,
        category: topic.category,
        tags: topic.tags,
        video: topic.video,
        published: topic.published,
      },
      helpId: topic.help_id || null,
      guideId: topic.help_id ? null : topic.guide_id,
    });
  };

  const deleteTopic = async (topic) => {
    const isOverride = topic.source === 'guide';
    const msg = isOverride
      ? `Reset "${topic.title}" to the built-in answer? Your edits and video will be removed.`
      : `Delete help entry "${topic.title}"?`;
    if (!window.confirm(msg)) return;
    try {
      await deleteHelpArticle(topic.help_id);
      fetchTopics();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSaved = async () => {
    const extra = editor?.onSavedExtra;
    setEditor(null);
    if (extra) await extra();
    fetchTopics();
  };

  const tabs = [
    { key: 'topics', label: 'Help topics' },
    { key: 'unanswered', label: 'Unanswered' },
    { key: 'tickets', label: 'From tickets' },
  ];

  return (
    <ModuleShell
      title="Help"
      icon={Question}
      subtitle="Ask a question, type a ticket number, or browse how-to topics"
      headerActions={canManage ? (
        <button
          type="button"
          onClick={() => setEditor({ initial: null })}
          className="btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          data-testid="help-add-button"
        >
          <Plus size={18} weight="bold" />
          Add Help Entry
        </button>
      ) : null}
    >
      <div data-testid="help-page">
        {canManage && (
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={tab === t.key ? 'btn-primary' : 'btn-secondary'}
                style={{ fontSize: '12px' }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {tab === 'unanswered' && canManage && (
          <UnansweredPanel
            onAnswer={(item, closeItem) => setEditor({
              initial: { question: item.question },
              heading: 'Answer this question',
              onSavedExtra: closeItem,
            })}
          />
        )}

        {tab === 'tickets' && canManage && <TicketSuggestionsPanel onApproved={fetchTopics} />}

        {tab === 'topics' && (
          <>
            <form onSubmit={handleAsk} style={{ ...cardStyle, display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <input
                className="input-field"
                placeholder="Ask a question, e.g. How do I raise a ticket? — or type a ticket number"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                maxLength={1000}
                data-testid="help-ask-input"
              />
              <button
                type="submit"
                className="btn-primary"
                disabled={asking || !question.trim()}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
              >
                <PaperPlaneTilt size={16} />
                {asking ? 'Searching…' : 'Ask'}
              </button>
            </form>

            {askError && <p style={{ fontSize: '12px', color: '#DC2626', marginBottom: '12px' }}>{askError}</p>}

            {result && <AnswerPanel result={result} canRaiseTicket={canRaiseTicket} canManage={canManage} onOpenTopic={openTopic} />}

            {tours.length > 0 && (
              <div style={{ ...cardStyle, marginBottom: '12px' }} data-testid="help-tours">
                <p style={{ fontSize: '12px', fontWeight: '600', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <PlayCircle size={16} color="#2563EB" weight="fill" /> Tutorial videos — watch each field explained on the real page
                </p>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {tours.map(({ id, tour }) => (
                    <button
                      key={id}
                      type="button"
                      className="btn-secondary"
                      style={{ fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      onClick={() => startHelpTour(id, { mode: 'video' })}
                    >
                      <PlayCircle size={14} weight="fill" color="#2563EB" /> {tour.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  className={c === category ? 'btn-primary' : 'btn-secondary'}
                  style={{ padding: '4px 10px', fontSize: '12px' }}
                >
                  {c}
                </button>
              ))}
            </div>

            <ListSearch
              value={search}
              onChange={setSearch}
              placeholder="Filter help topics…"
              resultCount={filtered.length}
              totalCount={topics.length}
            />

            {loading ? (
              <SkeletonBlock />
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={Question}
                title={topics.length ? 'No matching help topics' : 'No help topics yet'}
                description="Try another word, or ask your question in the box above."
              />
            ) : (
              <>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {topicPages.pageItems.map((t) => (
                    <TopicCard key={t.id} topic={t} canManage={canManage} onEdit={editTopic} onDelete={deleteTopic} />
                  ))}
                </div>
                <HelpPagination {...topicPages} />
              </>
            )}
          </>
        )}
      </div>

      {editor && (
        <HelpArticleForm
          initial={editor.initial}
          helpId={editor.helpId}
          guideId={editor.guideId}
          heading={editor.heading}
          onClose={() => setEditor(null)}
          onSaved={handleSaved}
        />
      )}
    </ModuleShell>
  );
}

export default HelpPage;
